import { Form, Input, Modal } from "antd";
import { useEffect, useState } from "react";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { useUpdateContextTip } from "../hooks/useUpdateContextTip";
import { QUERY_KEY_CONTEXT_TIPS } from "../hooks/useGetContextTips";
import { TContextTip } from "../types";
import { IconDropzone } from "./IconDropzone";

type Props = IdentifierProps & { tip: TContextTip };

export const EditContextTip = ({ open, handleClose, tip }: Props) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useUpdateContextTip();
  const [iconFile, setIconFile] = useState<File | null>(null);

  useEffect(() => {
    if (tip) {
      form.setFieldsValue({
        title: tip.title,
        subtitle: tip.subtitle ?? "",
      });
      setIconFile(null);
    }
  }, [tip, form]);

  const handleSubmit = (values: { title: string; subtitle?: string }) => {
    mutate(
      {
        token,
        id: tip.id,
        title: values.title,
        subtitle: values.subtitle,
        icon: iconFile,
      },
      {
        onError: (err: any) => {
          const res = err.response?.data;
          let description = "An error occurred, please try again.";
          if (res?.message && typeof res.message === "string") {
            description = res.message;
          }
          if (res?.errors && typeof res.errors === "object") {
            const first = Object.values(res.errors)[0];
            if (Array.isArray(first) && first[0]) {
              description = String(first[0]);
            }
          }
          openNotification({
            title: "Error",
            state: "error",
            description,
            duration: 6,
          });
        },
        onSuccess: () => {
          openNotification({
            title: "Success",
            state: "success",
            description: "Context tip updated successfully",
            duration: 4.5,
          });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONTEXT_TIPS] });
          handleClose();
        },
      },
    );
  };

  return (
    <Modal
      title="Edit context tip"
      open={open}
      onCancel={() => handleClose()}
      footer={null}
      width="36rem"
      style={{ top: 20 }}
      destroyOnClose
    >
      <Form
        layout="vertical"
        requiredMark={false}
        form={form}
        onFinish={handleSubmit}
      >
        <Form.Item name="title" label="Title" rules={generalValidationRules}>
          <Input placeholder="Complete your profile" />
        </Form.Item>
        <Form.Item name="subtitle" label="Subtitle">
          <Input.TextArea
            rows={2}
            placeholder="Add skills and experience to improve visibility"
          />
        </Form.Item>
        <Form.Item label="Icon" extra="Leave unchanged to keep the current icon.">
          <IconDropzone
            value={iconFile}
            existingUrl={tip.icon_url}
            onChange={setIconFile}
          />
        </Form.Item>
        <div className="flex justify-end gap-2 mt-4">
          <AppButton
            label="Cancel"
            variant="transparent"
            handleClick={() => handleClose()}
          />
          <AppButton label="Save changes" isLoading={isLoading} type="submit" />
        </div>
      </Form>
    </Modal>
  );
};
